import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CalendarClock, X, ArrowRight } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Year-end NIIT exposure banner. Renders on the Dashboard between Oct
// 1 and Dec 31 when ALL of these conditions hold:
//   1. The user is authenticated AND has saved NIIT prefs
//      (filing_status + magi).
//   2. magi > threshold for their filing status (i.e., NIIT actually
//      applies right now).
//   3. The user hasn't dismissed this year's banner via the X button.
//
// Dismissal is per-user, per-calendar-year so users see the prompt
// again next year without ever feeling spammed.

const FILING_LABEL = {
  single: 'Single / HoH',
  hoh:    'Single / HoH',
  mfj:    'MFJ',
  mfs:    'MFS',
};

export default function NiitYearEndBanner() {
  const [show, setShow] = useState(false);
  const [magiExcess, setMagiExcess] = useState(0);
  const [filingLabel, setFilingLabel] = useState('');

  useEffect(() => {
    let cancelled = false;
    const now = new Date();
    const month = now.getMonth(); // 0-indexed: Oct=9, Nov=10, Dec=11
    // Dev-only escape hatch: `?force_niit_banner=1` bypasses the
    // calendar gate so we can verify the layout out-of-season. The
    // back-end gates (auth + prefs + threshold + dismissal) still apply.
    // Safe to ship — the param is opt-in and the worst-case is a user
    // accidentally seeing their own NIIT prompt early.
    const forced = typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('force_niit_banner') === '1';
    if (month < 9 && !forced) return; // Oct (9) → Dec (11). Out of season — bail.
    const year = now.getFullYear();
    (async () => {
      try {
        const [prefsRes, dismissRes] = await Promise.all([
          axios.get(`${API}/users/me/niit-prefs`, { withCredentials: true }),
          axios.get(`${API}/users/me/dashboard-dismissals`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        const prefs = prefsRes.data || {};
        const dismissals = (dismissRes.data || {}).dismissals || {};
        const dismissedKey = `niit_year_end_${year}`;
        if (dismissals[dismissedKey]) return;
        const fs = prefs.filing_status;
        const magi = prefs.magi;
        const thresholds = prefs.thresholds || {};
        if (!fs || typeof magi !== 'number' || !thresholds[fs]) return;
        const threshold = thresholds[fs];
        if (magi <= threshold) return;
        setMagiExcess(magi - threshold);
        setFilingLabel(FILING_LABEL[fs] || fs.toUpperCase());
        setShow(true);
      } catch {
        // anon / unauth — silently skip
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleDismiss = async () => {
    setShow(false);
    const year = new Date().getFullYear();
    try {
      await axios.post(
        `${API}/users/me/dashboard-dismissals`,
        { key: `niit_year_end_${year}`, value: true },
        { withCredentials: true },
      );
    } catch {
      // tolerate the failure — the banner is already hidden in this
      // session; it'll just reappear on next reload if the post failed
    }
  };

  if (!show) return null;

  return (
    <div
      className="relative rounded-xl border-2 border-amber-300/70 dark:border-amber-600/50 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-950/40 dark:via-orange-950/30 dark:to-amber-950/40 p-4 mb-4"
      data-testid="niit-year-end-banner"
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss until next year"
        title="Dismiss for this year"
        className="absolute top-2 right-2 p-1 rounded-md hover:bg-amber-200/50 dark:hover:bg-amber-800/40 text-amber-700 dark:text-amber-300"
        data-testid="niit-year-end-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="shrink-0 w-9 h-9 rounded-full bg-amber-200/70 dark:bg-amber-800/40 flex items-center justify-center">
          <CalendarClock className="w-5 h-5 text-amber-700 dark:text-amber-300" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-amber-900 dark:text-amber-100">
            You&rsquo;re ${magiExcess.toLocaleString(undefined, { maximumFractionDigits: 0 })} over the {filingLabel} <AbbrTip term="NIIT">NIIT</AbbrTip> threshold for this year
          </p>
          <p className="text-[12px] text-amber-800 dark:text-amber-200/90 mt-1 leading-snug">
            Every dollar of taxable-account dividends above the line faces the{' '}
            <strong>3.8% Net Investment Income Tax</strong>. Year-end moves can pull
            your <AbbrTip term="MAGI">MAGI</AbbrTip> back under the threshold — Q4 401(k) catch-ups, traditional
            IRA contributions, harvested capital losses, and timing deductible
            expenses are the usual levers.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Link
              to="/year-end-report"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold shadow-sm transition-colors"
              data-testid="niit-year-end-banner-cta-report"
            >
              See my Year-End Snapshot <ArrowRight className="w-3 h-3" />
            </Link>
            <Link
              to="/learn/niit-surtax"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200 text-xs font-semibold hover:bg-amber-100/60 dark:hover:bg-amber-900/30 transition-colors"
              data-testid="niit-year-end-banner-cta-learn"
            >
              Learn about NIIT
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
